import { z } from "zod";
import { translations } from "@/i18n/translations";
import { supabase } from "@/integrations/supabase/client";

export const PLAN_COMPARISON_SETTING_KEY = "plan_comparison";

export const planComparisonRowSchema = z.object({
  label: z.string(),
  lite: z.string(),
  pro: z.string(),
  help: z.string(),
});

export const planComparisonLangSchema = z.object({
  // El panel ya no muestra título; se mantiene opcional para no invalidar contenidos guardados.
  title: z.string().optional(),
  cta: z.string(),
  footer: z.string(),
  rows: z.array(planComparisonRowSchema),
});

export const planComparisonSchema = z.object({
  es: planComparisonLangSchema,
  en: planComparisonLangSchema,
});

export type PlanComparisonRow = z.infer<typeof planComparisonRowSchema>;
export type PlanComparisonLang = z.infer<typeof planComparisonLangSchema>;
export type PlanComparisonContent = z.infer<typeof planComparisonSchema>;

const section = (translations as any).planComparison;

/**
 * Valores por defecto tomados de translations.ts: si la clave de configuración
 * no existe o el JSON guardado es inválido, el panel sigue mostrando el texto
 * original en lugar de quedarse vacío.
 */
export const DEFAULT_PLAN_COMPARISON: PlanComparisonContent = {
  es: {
    title: section.title.es,
    cta: section.cta.es,
    footer: section.footer.es,
    rows: section.rows.es as PlanComparisonRow[],
  },
  en: {
    title: section.title.en,
    cta: section.cta.en,
    footer: section.footer.en,
    rows: section.rows.en as PlanComparisonRow[],
  },
};

export const clonePlanComparison = (content: PlanComparisonContent): PlanComparisonContent =>
  JSON.parse(JSON.stringify(content)) as PlanComparisonContent;

export const parsePlanComparison = (value: unknown): PlanComparisonContent => {
  const parsed = planComparisonSchema.safeParse(value);
  return parsed.success ? parsed.data : clonePlanComparison(DEFAULT_PLAN_COMPARISON);
};

export const fetchPlanComparison = async (): Promise<PlanComparisonContent> => {
  const { data } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", PLAN_COMPARISON_SETTING_KEY)
    .maybeSingle();
  if (!data?.value) return clonePlanComparison(DEFAULT_PLAN_COMPARISON);
  return parsePlanComparison(data.value);
};

export const planComparisonQueryOptions = {
  queryKey: ["plan-comparison"] as const,
  queryFn: fetchPlanComparison,
  staleTime: 5 * 60 * 1000,
};
